import { AHButton, AHText, Box } from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";

import { LoginNavigationParamList } from "./navigation";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RouteProp } from "@react-navigation/core";
import { useLogin } from "./useLogin";
import { Lottie } from "../../components/Lottie";
import { MobileOnWeb } from "./WebLoginScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as Clipboard from "expo-clipboard";
import { decodeInvite, Invite } from "./clipboard";
import { useAppState } from "@/src/utils";
import { useIsMobileOnWeb } from "./useIsMobileOnWeb";

async function clearClipboard() {
  try {
    await Clipboard.setStringAsync("");
  } catch (e) {
    console.error(e);
  }
}

let hasTriedClipboard = false;

export const LoginScreen = (props: {
  navigation: NativeStackNavigationProp<LoginNavigationParamList, "Login">;
  route: RouteProp<LoginNavigationParamList, "Login">;
}) => {
  const routeOrg = useMemo(
    () =>
      props.route.params?.invitation && props.route.params?.organization
        ? {
            organization: props.route.params.organization,
            invitation: props.route.params.invitation,
            organization_name: props.route.params.organization_name,
            display_name: props.route.params.display_name,
          }
        : undefined,
    [
      props.route.params?.invitation,
      props.route.params?.organization_name,
      props.route.params?.organization,
      props.route.params?.display_name,
    ]
  );

  const [clipboardOrg, setClipboardOrg] = useState<Invite | null>(null);

  const active = useAppState() === "active";
  useEffect(() => {
    if (!routeOrg && active && Platform.OS !== "web" && !hasTriedClipboard) {
      hasTriedClipboard = true;
      Clipboard.getStringAsync()
        .then((content) => {
          if (content) {
            const decoded = decodeInvite(content);
            if (decoded) {
              setClipboardOrg(decoded);
              clearClipboard();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [routeOrg, active]);

  const org = routeOrg ?? clipboardOrg ?? undefined;
  const [_login, isLoggingIn, isReady, hasErrored] = useLogin(
    false,
    org,
    props.route.params?.email_hint
  );

  const login = useCallback(() => {
    _login();
  }, [_login]);

  const isMobileOnWeb = useIsMobileOnWeb();

  if (isMobileOnWeb) {
    return <MobileOnWeb invite={routeOrg} />;
  }

  return (
    <View
      style={{
        ...StyleSheet.absoluteFillObject,
        backgroundColor: "#FFF",
      }}
    >
      <View
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          flex: 1,
        }}
      >
        <View
          style={{
            width: "100%",
            flex: 1,
            zIndex: 3,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Lottie
            resizeMode="contain"
            autoPlay
            style={{
              width: 200,
            }}
            speed={1}
            loop={false}
            hardwareAccelerationAndroid
            source={require("./lottie-icon.json")}
          />
        </View>

        <View style={{ justifyContent: "center" }}>
          <AHText
            type="title"
            style={{
              fontSize: 24,
              textAlign: "center",
            }}
          >
            <FormattedMessage
              defaultMessage="The Heart of Store Operations"
              description="LoginScreen slogan"
            />
          </AHText>
        </View>
        <Box
          spaceHorizontal="large"
          style={{ width: "100%", flex: 1, maxWidth: 600 }}
        >
          <Box style={{ width: "100%", flex: 1 }}>
            <Lottie
              resizeMode="contain"
              autoPlay
              loop
              speed={1}
              hardwareAccelerationAndroid
              source={require("./lottie-waving-coworkers.json")}
            />
          </Box>
        </Box>
      </View>
      <Buttons
        orgName={
          hasErrored ? undefined : (org?.display_name ?? org?.organization_name)
        }
        login={login}
        isLoggingIn={isLoggingIn}
        isReady={isReady}
        onGroupDeviceLogin={() => props.navigation.navigate("GroupDeviceLogin")}
      />
    </View>
  );
};

function Buttons({
  login,
  isLoggingIn,
  isReady,
  onGroupDeviceLogin,
  orgName,
}: {
  login: () => void;
  isLoggingIn: boolean;
  isReady: boolean;
  onGroupDeviceLogin: () => void;
  orgName: string | undefined;
}) {
  const intl = useIntl();

  const insets = useSafeAreaInsets();
  return (
    <Box
      insets={{ bottom: insets.bottom }}
      style={{
        alignContent: "center",
        justifyContent: "center",

        alignItems: "center",
      }}
      spaceBottom="xlarge"
    >
      <AHButton
        themeType="primary"
        style={{
          zIndex: 1,
          width: "70%",
          minWidth: 100,
          maxWidth: 300,
          height: 50,
          marginBottom: 10,
        }}
        loading={isLoggingIn}
        onPress={() => {
          login();
        }}
        title={
          orgName
            ? intl.formatMessage(
                {
                  defaultMessage: "Accept invitation from {orgName}",
                  description: "LoginScreen accept invitation button",
                },
                {
                  orgName,
                }
              )
            : intl.formatMessage({
                defaultMessage: "Log in",
                description: "LoginScreen log in button",
              })
        }
      />

      <AHButton
        themeType="plain"
        style={{
          zIndex: 1,
          width: "70%",
          minWidth: 100,
          maxWidth: 300,
          height: 50,
          marginBottom: 10,
        }}
        onPress={onGroupDeviceLogin}
        title={intl.formatMessage({
          defaultMessage: "Log in as shared device",
          description: "LoginScreen log in as group device button",
        })}
      />
    </Box>
  );
}
